import type { JhHeat, JhOutcome, JhSignal, JhStatus } from '@/services/job-hunter.service';

export type Tone = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'primary';

export const flag = (cc?: string) =>
  cc && /^[A-Z]{2}$/.test(cc) ? String.fromCodePoint(...Array.from(cc).map((c) => 127397 + c.charCodeAt(0))) : '';

const COUNTRY_NAMES: Record<string, string> = {
  DE: 'Germany', NL: 'Netherlands', IT: 'Italy', PT: 'Portugal', IE: 'Ireland', ES: 'Spain', FR: 'France', AT: 'Austria', BE: 'Belgium',
  LU: 'Luxembourg', CH: 'Switzerland', PL: 'Poland', CZ: 'Czechia', SK: 'Slovakia', HU: 'Hungary', RO: 'Romania', BG: 'Bulgaria',
  GR: 'Greece', HR: 'Croatia', SI: 'Slovenia', DK: 'Denmark', SE: 'Sweden', NO: 'Norway', FI: 'Finland', EE: 'Estonia', LV: 'Latvia',
  LT: 'Lithuania', MT: 'Malta', CY: 'Cyprus', IS: 'Iceland', GB: 'United Kingdom', US: 'United States', CA: 'Canada', AU: 'Australia',
  NZ: 'New Zealand', AE: 'UAE', SA: 'Saudi Arabia', QA: 'Qatar', KW: 'Kuwait', BH: 'Bahrain', OM: 'Oman', PK: 'Pakistan', IN: 'India',
  SG: 'Singapore', JP: 'Japan', TR: 'Türkiye', EG: 'Egypt', RS: 'Serbia', UA: 'Ukraine', BR: 'Brazil', MX: 'Mexico', MY: 'Malaysia',
  ZA: 'South Africa', IL: 'Israel', HK: 'Hong Kong', CN: 'China', KR: 'South Korea',
};
export const countryName = (cc?: string) => (cc ? COUNTRY_NAMES[cc] ?? cc : '');

export function timeAgo(iso?: string | null): string {
  if (!iso) return '—';
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (Number.isNaN(s)) return '—';
  const future = s < 0;
  const a = Math.abs(s);
  const out = a < 60 ? `${a}s` : a < 3600 ? `${Math.round(a / 60)}m` : a < 86400 ? `${Math.round(a / 3600)}h` : a < 86400 * 45 ? `${Math.round(a / 86400)}d` : `${Math.round(a / (86400 * 30))}mo`;
  return future ? `in ${out}` : `${out} ago`;
}

export const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
export const fmtDateTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
export const num = (n?: number | null) => (n ?? 0).toLocaleString();

export function scoreTone(score?: number): { tone: Tone; color: string; label: string } {
  const s = score ?? 0;
  if (s >= 85) return { tone: 'success', color: 'var(--color-success)', label: 'Strong fit' };
  if (s >= 70) return { tone: 'primary', color: 'var(--color-primary)', label: 'Good fit' };
  if (s >= 50) return { tone: 'warning', color: 'var(--color-warning)', label: 'Partial fit' };
  return { tone: 'danger', color: 'var(--color-danger)', label: 'Weak fit' };
}

export const SIGNAL_META: Record<JhSignal, { label: string; short: string; tone: Tone; hint: string }> = {
  explicit: { label: 'Visa sponsored', short: 'Explicit', tone: 'success', hint: 'The posting itself offers visa sponsorship or relocation with visa support.' },
  likely: { label: 'Likely sponsor', short: 'Likely', tone: 'info', hint: 'Strong indirect signals: international team, relocation package, hires from abroad.' },
  none: { label: 'Not mentioned', short: 'None', tone: 'neutral', hint: 'The posting says nothing about sponsorship.' },
  excluded: { label: 'No sponsorship', short: 'Excluded', tone: 'danger', hint: 'The posting requires an existing right to work or says no sponsorship.' },
};

export const STATUS_META: Record<JhStatus, { label: string; tone: Tone; column?: string }> = {
  proposed: { label: 'New match', tone: 'primary' },
  saved: { label: 'Saved', tone: 'info', column: 'saved' },
  approved: { label: 'Preparing', tone: 'warning', column: 'approved' },
  needs_input: { label: 'Needs your input', tone: 'warning', column: 'needs_input' },
  ready: { label: 'Ready to submit', tone: 'success', column: 'ready' },
  applied: { label: 'Applied', tone: 'info', column: 'applied' },
  interview: { label: 'Interview', tone: 'success', column: 'interview' },
  offer: { label: 'Offer', tone: 'success', column: 'offer' },
  rejected: { label: 'Rejected', tone: 'danger', column: 'closed' },
  skipped: { label: 'Skipped', tone: 'neutral' },
  withdrawn: { label: 'Withdrawn', tone: 'neutral', column: 'closed' },
  expired: { label: 'Expired', tone: 'neutral' },
};

export const HEAT_META: Record<JhHeat, { label: string; tone: Tone; hint: string }> = {
  hot: { label: 'Hot', tone: 'danger', hint: 'Produced a match in the last 14 days — polled every 6 h' },
  warm: { label: 'Warm', tone: 'warning', hint: 'Relevant titles in the last 30 days — polled daily' },
  cold: { label: 'Cold', tone: 'info', hint: 'Nothing relevant lately — polled weekly' },
  dead: { label: 'Dead', tone: 'neutral', hint: 'Board gone or failing — not polled until revived' },
};

export const OUTCOME_META: Record<JhOutcome | 'unscreened', { label: string; tone: Tone }> = {
  proposed: { label: 'Proposed', tone: 'success' },
  queued: { label: 'Queued for AI', tone: 'info' },
  below_threshold: { label: 'Below the bar', tone: 'warning' },
  rejected: { label: 'Filtered out', tone: 'neutral' },
  error: { label: 'AI error', tone: 'danger' },
  unscreened: { label: 'Not screened', tone: 'neutral' },
};

export const FEEDBACK_LABELS: Record<string, string> = {
  not_relevant: 'Not relevant', no_sponsorship: 'No sponsorship', too_senior: 'Too senior', too_junior: 'Too junior',
  location: 'Wrong location', salary: 'Salary', language: 'Language requirement', already_applied: 'Already applied', other: 'Other',
};

export const REMOTE_LABEL: Record<string, string> = { onsite: 'On-site', hybrid: 'Hybrid', remote: 'Remote', unknown: 'Workplace n/a' };

export const BELOW_REASON_LABEL: Record<string, string> = {
  low_score: 'Fit below your minimum',
  no_sponsorship: 'Posting excludes sponsorship',
  no_sponsorship_signal: 'No sponsorship signal',
  excluded_country: 'Excluded country',
  country_not_targeted: 'Country not in this agent’s targets',
  found_by_other_agent: 'Already a match from another agent',
  added_by_hand: 'Added by hand',
};

export const TONE_CLASS: Record<Tone, string> = {
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
  info: 'bg-info-soft text-info',
  neutral: 'bg-surface-alt text-text-muted',
  primary: 'bg-primary-soft text-primary',
};

export const initials = (name?: string) =>
  (name ?? '?').replace(/[^A-Za-z0-9 ]/g, ' ').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('') || '?';

/** A stable hue per employer so avatars are recognisable at a glance. */
export function hueOf(name?: string) {
  let h = 0;
  for (const c of name ?? '') h = (h * 31 + c.charCodeAt(0)) % 360;
  return h;
}

export async function copyText(text: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
}

/** Agent accent colours (the backend stores the name). */
/** Muted tones that sit with the brand gold. Gold is the default. */
export const AGENT_COLORS: Record<string, string> = {
  gold: '#C8A55B', emerald: '#3F8F6B', sky: '#3B82B0', rose: '#C0566B', teal: '#2F8F8A', orange: '#C97A3D', violet: '#7A6AB8', slate: '#5B6573',
};
/** Earlier palette names: `indigo` was the old default (shown as gold now), `amber` reads as gold too. */
const LEGACY: Record<string, string> = { indigo: AGENT_COLORS.gold, amber: AGENT_COLORS.gold };
export const agentHex = (color?: string) => AGENT_COLORS[color ?? ''] ?? LEGACY[color ?? ''] ?? AGENT_COLORS.gold;
export const agentSoft = (color?: string, pct = 14) => `color-mix(in srgb, ${agentHex(color)} ${pct}%, transparent)`;

export const fmtBytes = (n?: number) => (!n ? '—' : n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);

/** CV health tone: the same bands as the fit score. */
export const healthTone = (score?: number | null) => scoreTone(score ?? 0);

export const CV_CATEGORY_LABEL: Record<string, string> = {
  ats: 'ATS readability', impact: 'Impact & metrics', clarity: 'Clarity & writing', relevance: 'Relevance to target roles', keywords: 'Keywords',
};
